import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useDebounce } from '@aimsos/hooks';

import { listWorkspaces } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { buildWorkspaceListBody } from '../../constants';
import { mapWorkspaceListItemToUi } from '../../utils';

const DEFAULT_META = {
  total_items: 0,
  total_pages: 1,
  current_page: 1,
  per_page: 15,
  has_next: false,
  has_previous: false,
};

/**
 * Workspace list: pagination, debounced search, POST /v1/workspace/list.
 * Used by {@link WorkspacesProvider} so API access stays in hooks.
 */
export function useWorkspacesList() {
  const { t } = useTranslation('workspaces');
  const [workspaces, setWorkspaces] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [meta, setMeta] = useState(DEFAULT_META);
  const [listSearchKeyword, setListSearchKeyword] = useState('');
  const debouncedListSearchKeyword = useDebounce(listSearchKeyword, 350);
  const prevDebouncedListSearchRef = useRef(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const refetchWorkspaces = useCallback(() => {
    setListRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (prevDebouncedListSearchRef.current === null) {
      prevDebouncedListSearchRef.current = debouncedListSearchKeyword;
      return;
    }
    if (prevDebouncedListSearchRef.current !== debouncedListSearchKeyword) {
      prevDebouncedListSearchRef.current = debouncedListSearchKeyword;
      startTransition(() => setPage(1));
    }
  }, [debouncedListSearchKeyword]);

  useEffect(() => {
    let cancelled = false;
    startTransition(() => {
      setListLoading(true);
      setListError(null);
    });

    listWorkspaces(buildWorkspaceListBody(page, pageSize, debouncedListSearchKeyword))
      .then((res) => {
        if (cancelled) return;
        const normalized = validateApiResponse(res, t('list.table.error'));
        const rows = Array.isArray(normalized.body) ? normalized.body : [];
        setWorkspaces(rows.map(mapWorkspaceListItemToUi));
        setMeta({
          total_items: Number(normalized.meta?.total_items ?? 0) || 0,
          total_pages: Math.max(1, Number(normalized.meta?.total_pages ?? 1) || 1),
          current_page: Number(normalized.meta?.current_page ?? page) || page,
          per_page: Number(normalized.meta?.per_page ?? pageSize) || pageSize,
          has_next: Boolean(normalized.meta?.has_next),
          has_previous: Boolean(normalized.meta?.has_previous),
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setListError(err?.message ?? t('common.requestFailed'));
        setWorkspaces([]);
        setMeta({ ...DEFAULT_META, current_page: page, per_page: pageSize });
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, debouncedListSearchKeyword, listRefreshKey, t]);

  const setPageSizeAndReset = useCallback((nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  }, []);

  const getWorkspace = useCallback(
    (id) => workspaces.find((ws) => ws.id === id) ?? null,
    [workspaces],
  );

  return useMemo(
    () => ({
      workspaces,
      listLoading,
      listError,
      listRefreshKey,
      meta,
      page,
      setPage,
      pageSize,
      setPageSize: setPageSizeAndReset,
      getWorkspace,
      listSearchKeyword,
      setListSearchKeyword,
      refetchWorkspaces,
    }),
    [
      workspaces,
      listLoading,
      listError,
      listRefreshKey,
      meta,
      page,
      pageSize,
      setPageSizeAndReset,
      getWorkspace,
      listSearchKeyword,
      refetchWorkspaces,
    ],
  );
}
